import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ASSET_KINDS, newId, type AssetKind } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssetTypes } from '../data/fleet.ts'
import { BackLink, Banner, Field, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'

/** The club's own asset categories. They refine the six base kinds; the prestart-check flag is used from M4 on. */
export function TypesPage() {
  const { t, i18n } = useTranslation()
  const { send } = useClub()
  const types = useAssetTypes()
  const access = useWriteAccess('asset.manage')
  const { busy, error, run } = useAction()
  const typeId = useRef(newId())
  const [f, setF] = useState({ code: '', kind: 'machine' as AssetKind, fr: '', en: '', prestart: false })
  const lang = i18n.language === 'en' ? 'en' : 'fr'
  const sorted = [...(types ?? [])].sort((a, b) => a.code.localeCompare(b.code))
  return (
    <section className="flex flex-col gap-4">
      <BackLink to="/assets">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('types.title')}</h1>
      <p className="meta">{t('types.help')}</p>
      {types && sorted.length === 0 && <p className="opacity-70">{t('types.none')}</p>}
      <ul className="flex flex-col gap-2" data-testid="type-list">
        {sorted.map((ty) => (
          <li key={ty.id} className="card flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold">{ty.label[lang]}</p>
              <p className="meta font-mono">{ty.code} · {t(`assets.kinds.${ty.kind}`)}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              {ty.requires_prestart_check && <Pill tone="green">{t('types.prestart')}</Pill>}
              {ty._pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
            </div>
          </li>
        ))}
      </ul>
      {!access.permitted ? <Banner tone="info">{t('types.notAllowed')}</Banner> : (
        <form className="card flex flex-col gap-3" onSubmit={(e) => {
          e.preventDefault()
          void run(async () => {
            await send('asset_type.define_local@1', { type_id: typeId.current, code: f.code.trim(), kind: f.kind, label: { fr: f.fr.trim(), en: f.en.trim() }, requires_prestart_check: f.prestart })
            typeId.current = newId(); setF({ code: '', kind: 'machine', fr: '', en: '', prestart: false })
          })
        }}>
          <h2 className="text-lg font-bold">{t('types.new')}</h2>
          <Field label={t('types.code')} hint={t('types.codeHelp')} htmlFor="type-code"><input id="type-code" className="input" required maxLength={40} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
          <Field label={t('types.kind')} htmlFor="type-kind">
            <select id="type-kind" className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as AssetKind })}>{ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}</select>
          </Field>
          <Field label={t('types.labelFr')} htmlFor="type-fr"><input id="type-fr" className="input" required maxLength={80} value={f.fr} onChange={(e) => setF({ ...f, fr: e.target.value })} /></Field>
          <Field label={t('types.labelEn')} htmlFor="type-en"><input id="type-en" className="input" required maxLength={80} value={f.en} onChange={(e) => setF({ ...f, en: e.target.value })} /></Field>
          <label className="flex min-h-14 items-center gap-3 text-base font-medium"><input type="checkbox" className="size-6" checked={f.prestart} onChange={(e) => setF({ ...f, prestart: e.target.checked })} />{t('types.prestart')}</label>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy || access.readOnlyMode} data-testid="type-add">{t('types.add')}</button>
        </form>
      )}
    </section>
  )
}
